import { AdminPermissionDTO } from "@/gen/api"

export const ADMIN_PERMISSION_ACTIONS = ["read_own", "read_all", "write_own", "write_all"] as const

export type AdminPermissionAction = (typeof ADMIN_PERMISSION_ACTIONS)[number]

export const ADMIN_PERMISSION_OBJECTS = [
  { object: "dashboard", label: "Dashboard" },
  { object: "admin_management", label: "Admin Management" },
  { object: "water_bureaus", label: "Water Bureaus" },
  { object: "devices", label: "Devices" },
  { object: "customers", label: "Customers" },
  { object: "water_efficiency", label: "Water Efficiency" },
  { object: "app_version", label: "App Version" },
] as const

export type AdminPermissionObject = (typeof ADMIN_PERMISSION_OBJECTS)[number]["object"]

export const ADMIN_PERMISSION_COLUMNS: { action: AdminPermissionAction; label: string }[] = [
  { action: "read_own", label: "Read Own" },
  { action: "read_all", label: "Read All / Read Only" },
  { action: "write_own", label: "Write Own" },
  { action: "write_all", label: "Write All" },
]

export const ADMIN_ROLE_PRESETS = ["super_admin", "admin", "read_only"] as const

export type AdminRolePreset = (typeof ADMIN_ROLE_PRESETS)[number]

const sameScope = (permission: AdminPermissionDTO, councilId?: number) =>
  councilId == null ? permission.councilId == null : permission.councilId === councilId

const isMatrixPermission = (permission: AdminPermissionDTO, councilId?: number) =>
  ADMIN_PERMISSION_OBJECTS.some((option) => option.object === permission.object) &&
  ADMIN_PERMISSION_ACTIONS.includes(permission.action as AdminPermissionAction) &&
  sameScope(permission, councilId)

export const hasPermission = (
  permissions: AdminPermissionDTO[] = [],
  object: AdminPermissionObject,
  action: AdminPermissionAction,
  councilId?: number,
) =>
  permissions.some(
    (permission) =>
      permission.object === object &&
      permission.action === action &&
      sameScope(permission, councilId),
  )

export const hasReadPermission = (
  permissions: AdminPermissionDTO[] = [],
  object: AdminPermissionObject,
  councilId?: number,
) =>
  hasPermission(permissions, object, "read_own", councilId) ||
  hasPermission(permissions, object, "read_all", councilId)

export type AdminPermissionSelection = Record<AdminPermissionObject, AdminPermissionAction[]>

export const emptyPermissionSelection = (): AdminPermissionSelection =>
  Object.fromEntries(
    ADMIN_PERMISSION_OBJECTS.map(({ object }) => [object, []]),
  ) as unknown as AdminPermissionSelection

export const getPermissionSelection = (
  permissions: AdminPermissionDTO[] = [],
  councilId?: number,
) => {
  const selection = emptyPermissionSelection()
  ADMIN_PERMISSION_OBJECTS.forEach(({ object }) => {
    selection[object] = ADMIN_PERMISSION_ACTIONS.filter((action) =>
      hasPermission(permissions, object, action, councilId),
    )
  })
  return selection
}

export const mergePermissionSelection = (
  permissions: AdminPermissionDTO[] = [],
  selection: AdminPermissionSelection,
  councilId?: number,
): AdminPermissionDTO[] => {
  const remaining = permissions.filter((permission) => !isMatrixPermission(permission, councilId))

  return [
    ...remaining,
    ...ADMIN_PERMISSION_OBJECTS.flatMap(({ object }) =>
      (selection[object] ?? []).map((action) => ({ object, action, councilId })),
    ),
  ]
}

export const mergePermissionSelectionForCouncils = (
  permissions: AdminPermissionDTO[] = [],
  selection: AdminPermissionSelection,
  councilIds: number[],
): AdminPermissionDTO[] => {
  const selectedCouncilIds = [...new Set(councilIds)]
  const remaining = permissions.filter(
    (permission) =>
      !selectedCouncilIds.some((councilId) => isMatrixPermission(permission, councilId)),
  )

  return [
    ...remaining,
    ...selectedCouncilIds.flatMap((councilId) =>
      ADMIN_PERMISSION_OBJECTS.flatMap(({ object }) =>
        (selection[object] ?? []).map((action) => ({ object, action, councilId })),
      ),
    ),
  ]
}

export const permissionSelectionForRole = (role: AdminRolePreset): AdminPermissionSelection => {
  const selection = emptyPermissionSelection()
  const actions: AdminPermissionAction[] =
    role === "read_only" ? ["read_own", "read_all"] : [...ADMIN_PERMISSION_ACTIONS]
  ADMIN_PERMISSION_OBJECTS.forEach(({ object }) => {
    selection[object] = actions
  })
  return selection
}

export const permissionSelectionForRoles = (roles: AdminRolePreset[]): AdminPermissionSelection => {
  const selection = emptyPermissionSelection()
  roles.forEach((role) => {
    const preset = permissionSelectionForRole(role)
    ADMIN_PERMISSION_OBJECTS.forEach(({ object }) => {
      selection[object] = Array.from(new Set([...(selection[object] ?? []), ...preset[object]]))
    })
  })
  return selection
}

export const allInheritanceObjects = () => ADMIN_PERMISSION_OBJECTS.map(({ object }) => object)
